import React from "react";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import AddressManager from "../components/AddressManager";
import {
  User,
  Mail,
  Phone,
  Crown,
  Settings,
  Heart,
  Calendar,
} from "lucide-react";

const Account = () => {
  const { user } = useAuth();

  // Get user data from multiple possible sources
  const userName = user?.first_name || user?.name || "User";
  const userEmail = user?.email || "Not provided";
  const userPhone = user?.phone_number || user?.phone || "Not provided";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-12 h-12 text-orange-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome, {userName}!
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your health profile and subscription
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-orange-600" />
                  <span>Profile Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Fields */}
                <div className="space-y-4">
                  <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-500">
                        Full Name
                      </p>
                      <p className="text-lg font-semibold text-gray-900 truncate">
                        {userName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-500">
                        Email Address
                      </p>
                      <p className="text-lg font-semibold text-gray-900 truncate">
                        {userEmail}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-500">
                        Phone Number
                      </p>
                      <p className="text-lg font-semibold text-gray-900 truncate">
                        {userPhone}
                      </p>
                    </div>
                  </div>
                </div>

                <Button variant="outline" className="w-full">
                  <Settings className="w-4 h-4 mr-2" />
                  Edit Profile Information
                </Button>
              </CardContent>
            </Card>

            {/* Address Management */}
            <AddressManager />

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  <span>Recent Activity</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-center py-6">
                    <p className="text-gray-500 text-sm">No recent activity yet</p>
                    <p className="text-gray-400 text-xs mt-1">
                      Your future activities will appear here
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subscription & Quick Actions */}
          <div className="space-y-6">
            {/* Subscription Status */}
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center space-x-2 text-orange-700">
                  <Crown className="w-5 h-5" />
                  <span>Subscription</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <Badge className="bg-green-100 text-green-700 text-lg px-4 py-2 mb-3">
                    FREE ACTIVE
                  </Badge>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Premium Benefits
                  </h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>50% OFF on lab tests</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Free home sample collection</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Priority doctor booking</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Personal health tracking</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 justify-start">
                  <Heart className="w-4 h-4 mr-2" />
                  View Health Vitals
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Lab Test
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Phone className="w-4 h-4 mr-2" />
                  Find Doctors
                </Button>
                <a
                  href="https://wa.me/917337541113?text=Need help with my GEETH365 account"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    variant="outline"
                    className="w-full justify-start border-green-200 text-green-600 hover:bg-green-50"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    WhatsApp Support
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Health Summary */}
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardHeader>
                <CardTitle className="text-purple-700">
                  Health Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>BMI Status:</span>
                    <Badge className="bg-green-100 text-green-700">
                      Normal
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Last Checkup:</span>
                    <span className="text-gray-600">2 weeks ago</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Vitals Tracked:</span>
                    <span className="font-medium">6 metrics</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
